// One confirm dialog for every destructive or expensive console action
// (delete a key, retry all, rebuild, cleanup) — replaces the
// `window.confirm` calls and the deletes that asked nothing at all.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel, destructive = false, onConfirm, children }) {
  const { t } = useTranslation('console');
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm?.();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-md" role="alertdialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {t('confirm.cancel')}
          </Button>
          <Button variant={destructive ? 'destructive' : 'default'} onClick={confirm} disabled={busy} data-testid="confirm-action">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {confirmLabel || t('confirm.ok')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
